import { useLayoutEffect, useRef } from "react";
import {
  ObjectDetailsPanel,
  getObjectStatusColor,
} from "../ObjectDetailsPanel";
import { useObjectDetailsPanel } from "../ObjectDetailsPanel/hooks";
import { ObjectOverlaysProps } from "./types";
import {
  addHoverHandlers,
  clearScene,
  computeBoxGeometry,
  computeEdgeRotation,
  computeTopCenter,
  createEdgePlane,
  createHitPlane,
  createLabel,
  mapBndBoxToSphereCorners,
} from "./utils";

export const ObjectOverlays = ({
  loadedImageIndex,
  cameraPositions,
  activeObjectId,
  cameraRef,
  imageWidth,
  imageHeight,
  onSelectObject,
}: ObjectOverlaysProps) => {
  const rectRef = useRef<any>(null);

  const targetObjectId = activeObjectId
    ? isNaN(+activeObjectId)
      ? activeObjectId
      : +activeObjectId
    : null;

  const {
    openObjectDetailsPanel,
    closeObjectDetailsPanel,
    objectDetailsPanel,
  } = useObjectDetailsPanel();

  useLayoutEffect(() => {
    if (
      !rectRef.current ||
      loadedImageIndex === null ||
      loadedImageIndex === false
    ) {
      return;
    }

    const scene = rectRef.current;

    clearScene(scene);

    const imageIndex = loadedImageIndex as number;

    cameraPositions[imageIndex]?.objects?.forEach((object: any) => {
      const edgePlanes: HTMLElement[] = [];
      const { topLeft, topRight, bottomRight, bottomLeft } =
        mapBndBoxToSphereCorners(object.bndbox, imageWidth, imageHeight);

      const lines = [
        [topLeft, topRight],
        [topRight, bottomRight],
        [bottomRight, bottomLeft],
        [bottomLeft, topLeft],
      ] as const;

      const edgeColor = getObjectStatusColor({
        status: object.detectedObjectId.status,
        isActive: object.detectedObjectId._id === targetObjectId,
      });

      const handleClick = () => {
        openObjectDetailsPanel({ data: object });
        // eslint-disable-next-line no-console
        console.log("Clicked object ID:", object.detectedObjectId._id);
      };

      lines.forEach(([start, end]) => {
        const plane = createEdgePlane(
          scene,
          start,
          end,
          edgeColor,
          handleClick,
        );
        edgePlanes.push(plane);
      });

      const { center, width, height, rotation } = computeBoxGeometry(
        topLeft,
        topRight,
        bottomLeft,
      );

      const hitPlane = createHitPlane(
        scene,
        center,
        width,
        height,
        rotation,
        edgeColor,
        handleClick,
      );

      const topCenter = computeTopCenter(topLeft, topRight);
      const edgeRotation = computeEdgeRotation(topLeft, topRight);

      const label = createLabel(
        scene,
        object.detectedObjectId.type,
        edgeColor,
        topCenter,
        edgeRotation,
      );

      addHoverHandlers(
        hitPlane,
        label,
        edgePlanes,
        edgeColor,
        objectDetailsPanel.data?.detectedObjectId?._id ===
          object.detectedObjectId._id,
      );
    });

    // Auto-pan camera to center on the active object by converting its bounding box center to spherical rotation angles
    if (cameraRef.current && targetObjectId && imageWidth && imageHeight) {
      const activeObject = cameraPositions[imageIndex]?.objects?.find(
        (object: any) => object.detectedObjectId._id === targetObjectId,
      );
      if (!activeObject) return;

      const { xmin, ymin, xmax, ymax } = activeObject.bndbox;
      const midX = (xmin + xmax) / 2;
      const midY = (ymin + ymax) / 2;
      const halfWidth = imageWidth / 2;
      const halfHeight = imageHeight / 2;
      const offsetX = ((halfWidth - midX) / halfWidth) * Math.PI;
      const offsetY = ((halfHeight - midY) / halfHeight) * (Math.PI / 2);
      const controls = cameraRef.current.components?.["custom-look-controls"];

      if (controls) {
        controls.pitchObject.rotation.x = offsetY;
        controls.yawObject.rotation.y = offsetX;
      }
    }
  }, [
    loadedImageIndex,
    targetObjectId,
    cameraPositions,
    cameraRef,
    imageWidth,
    imageHeight,
    onSelectObject,
    openObjectDetailsPanel,
    objectDetailsPanel.data?.detectedObjectId?._id,
  ]);

  if (loadedImageIndex === null) return null;

  return (
    <>
      <a-entity ref={rectRef} />
      {objectDetailsPanel.isOpen && (
        <ObjectDetailsPanel
          id={objectDetailsPanel.data.detectedObjectId._id}
          type={objectDetailsPanel.data.detectedObjectId.type}
          status={objectDetailsPanel.data.detectedObjectId.status}
          position={objectDetailsPanel.data.position}
          chessboardId={objectDetailsPanel.data.detectedObjectId.chessboard_id}
          onSelectObject={onSelectObject}
          onClose={closeObjectDetailsPanel}
        />
      )}
    </>
  );
};
